import React, { useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import { labelChangeStatus, labelChangeStatusTransition } from '../changeLabels';
import {
  buildStatusWaitAlert,
  describeStatusFlow,
  formatWaitReason,
  forwardBlocked,
} from '../changeStatusWait';
import ChangeStatusStepper from './ChangeStatusStepper';

interface Props {
  changeId: number;
  currentStatus: string;
  allowed: string[];
  blocked?: Array<{ status: string; reason: string }>;
  /** Nomes concretos ainda pendentes (áreas, gestor, subtarefas). */
  missingItems?: string[];
  /** Dono, solicitante (em status editáveis), criador, responsável ou gestor de mudanças. */
  canMutateChange?: boolean;
  onUpdated: () => Promise<void> | void;
}

const ChangeStatusPanel: React.FC<Props> = ({
  changeId,
  currentStatus,
  allowed,
  blocked = [],
  missingItems = [],
  canMutateChange = false,
  onUpdated,
}) => {
  const { showAlert, showConfirm } = useModal();
  const [selected, setSelected] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const canPickNext = canMutateChange && allowed.length > 0;
  const waitingForward = forwardBlocked(blocked);
  const flow = describeStatusFlow(currentStatus, allowed, blocked);
  const waitingReason = formatWaitReason(waitingForward[0]?.reason || '');
  const listedMissing = waitingReason.items.length
    ? waitingReason.items
    : missingItems.filter(Boolean);
  const cancelling = selected === 'cancelada';

  const explainWait = () => {
    const alert = buildStatusWaitAlert({ currentStatus, allowed, blocked, missingItems });
    showAlert({
      title: alert.title,
      message: alert.message,
      details: alert.details,
      variant: 'warning',
    });
  };

  const handleUpdate = async () => {
    if (!selected) {
      explainWait();
      return;
    }
    if (selected === 'cancelada') {
      const ok = await showConfirm({
        title: 'Cancelar mudança',
        message:
          'Tem certeza que deseja cancelar esta mudança? E-mails agendados, aprovações pendentes e subtarefas ativas serão cancelados. Chamados no Incidente, se houver, permanecem abertos.',
        variant: 'danger',
        confirmLabel: 'Cancelar mudança',
        cancelLabel: 'Voltar',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      await changesApi.updateStatus(changeId, selected);
      const cancelled = selected === 'cancelada';
      setSelected('');
      await onUpdated();
      if (cancelled) {
        showAlert({
          title: 'Mudança cancelada',
          message: 'A mudança foi cancelada com sucesso.',
          variant: 'success',
        });
      }
    } catch (err: unknown) {
      showAlert({
        title: 'Não foi possível atualizar o status',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o status.'),
        variant: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-3 w-100">
      <ChangeStatusStepper currentStatus={currentStatus} />
      <div className="change-status-panel__controls d-flex flex-column align-items-stretch align-items-md-end gap-1">
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-lg-end">
          <Badge
            bg={
              currentStatus === 'encerrar'
                ? 'success'
                : currentStatus === 'cancelada'
                  ? 'danger'
                  : 'primary'
            }
            className="fs-6 fw-semibold px-2 py-1"
          >
            {currentStatus === 'encerrar' ? 'Encerrada' : labelChangeStatus(currentStatus)}
          </Badge>
          <span className="small text-muted d-none d-md-inline">
            {currentStatus === 'encerrar'
              ? 'concluída'
              : currentStatus === 'cancelada'
                ? 'cancelada'
                : 'agora'}
          </span>
        </div>
        {canMutateChange ? (
          <>
            <Form.Label htmlFor="change-status-select" className="small mb-0 text-lg-end">
              Ir para outro status
            </Form.Label>
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-lg-end">
              <Form.Select
                id="change-status-select"
                size="sm"
                style={{ minWidth: 220 }}
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                disabled={!canPickNext}
                aria-label={canPickNext ? 'Status da mudança' : 'Status atual'}
              >
                <option value="">
                  {flow.currentLabel} (atual)
                </option>
                {allowed.map((status) => (
                  <option key={status} value={status}>
                    {labelChangeStatusTransition(status)}
                  </option>
                ))}
              </Form.Select>
              <Button
                size="sm"
                variant={cancelling ? 'danger' : 'primary'}
                onClick={() => void handleUpdate()}
                disabled={saving || (canPickNext && !selected && !waitingForward.length)}
              >
                {cancelling
                  ? 'Cancelar mudança'
                  : selected
                    ? 'Atualizar status'
                    : waitingForward.length
                      ? 'O que falta para avançar'
                      : canPickNext
                        ? 'Selecione o próximo'
                        : 'O que falta para avançar'}
              </Button>
            </div>
          </>
        ) : (
          <div className="small text-muted text-lg-end">
            Somente administrador, gestor de mudanças, dono, solicitante (em Avaliar/Agendado), criador
            ou responsável pela mudança podem alterar o status.
          </div>
        )}
        <div className="small text-muted text-lg-end">{flow.summary}</div>
        {waitingReason.intro || listedMissing.length ? (
          <div className="small text-warning text-lg-end">
            <div>{waitingReason.intro || (listedMissing.length ? 'Ainda falta:' : '')}</div>
            {listedMissing.length ? (
              <ul className="mb-0 mt-1 text-start d-inline-block">
                {listedMissing.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ChangeStatusPanel;
